import React from 'react';
import { FileText, Info, Download } from 'lucide-react';
import { ArcaPlan } from '../../constants/arcaPlans';
import { INDIVIDUAL_PLANS_CONTENT } from '../../data/individualPlansContent';

interface PlanDocumentsSectionProps {
	plan: ArcaPlan;
	translate: (key: string) => string;
}

/**
 * Documentación oficial del plan: KIID (si hay URL) y ficha del tipo de plan (siempre).
 * En desktop se muestran como dos cards a lo ancho (rejilla de 2 columnas); en mobile
 * se apilan.
 */
const PlanDocumentsSection: React.FC<PlanDocumentsSectionProps> = ({ plan, translate }) => {
	// Documento oficial del plan: el KIID del catálogo si algún día llega, si no el DFP
	// que ya tenemos enlazado en el contenido de planes individuales.
	const docUrl = plan.kiidUrl ?? INDIVIDUAL_PLANS_CONTENT[plan.catalogSlug]?.dfpUrl;

	const isIndexed = /indexa/i.test(plan.name) || /indexad/i.test(plan.style);

	const typeTitle = isIndexed
		? translate('individual.planDocs.indexed.title') || 'Gestión indexada'
		: translate('individual.planDocs.active.title') || 'Gestión activa (valor)';
	const typeSubtitle = isIndexed
		? translate('individual.planDocs.indexed.subtitle') ||
			'Replica el mercado global con comisiones muy bajas'
		: translate('individual.planDocs.active.subtitle') ||
			'Un equipo gestor selecciona las empresas en las que invertir';

	return (
		<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
			{/* Documento oficial del plan (DFP/KIID) — solo si hay URL */}
			{docUrl && (
				<a
					href={docUrl}
					target="_blank"
					rel="noopener noreferrer"
					className="surface-card p-4 flex items-center gap-3 hover:bg-muted/30 transition-colors"
				>
					<FileText className="w-5 h-5 text-primary shrink-0" />
					<div className="flex-1 min-w-0">
						<p className="text-sm font-medium">
							{translate('individual.planDocs.kiid.title') ||
								'Datos fundamentales para el partícipe'}
						</p>
						<p className="text-xs text-muted-foreground truncate">
							{(
								translate('individual.planDocs.kiid.subtitle') || 'Documento oficial de {plan}'
							).replace('{plan}', plan.name)}
						</p>
					</div>
					<Download className="w-4 h-4 text-primary shrink-0" />
				</a>
			)}

			{/* Perfil de riesgo: pastilla oculta temporalmente (el dato sigue en plan.riskProfile). */}

			{/* Tipo de gestión — informativo (sin modal) */}
			<div className="surface-card p-4 flex items-center gap-3">
				<Info className="w-5 h-5 text-primary shrink-0" />
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium">{typeTitle}</p>
					<p className="text-xs text-muted-foreground">{typeSubtitle}</p>
				</div>
			</div>
		</div>
	);
};

export default PlanDocumentsSection;
